import React, { createContext, useContext, useState, useCallback } from 'react';
import Alert from '../components/Alert';

interface AlertOptions {
    type?: 'success' | 'error' | 'warning' | 'info' | 'primary';
    title?: string;
    confirmable?: boolean;
    showOkButton?: boolean;
    confirmText?: string;
    cancelText?: string;
    okText?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
    onOk?: () => void;
    dismissible?: boolean;
    confirmVariant?: string;
    cancelVariant?: string;
    okVariant?: string;
}

interface AlertContextType {
    showAlert: (message: string, options?: AlertOptions) => void;
    hideAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [alertState, setAlertState] = useState<{
        show: boolean;
        message: string;
        options: AlertOptions;
    }>({
        show: false,
        message: '',
        options: {},
    });

    const showAlert = useCallback((message: string, options: AlertOptions = {}) => {
        setAlertState({
            show: true,
            message,
            options,
        });
    }, []);

    const hideAlert = useCallback(() => {
        setAlertState((prev) => ({ ...prev, show: false }));
    }, []);

    const handleClose = () => {
        if (alertState.options.onCancel) {
            alertState.options.onCancel();
        }
        hideAlert();
    };

    const handleConfirm = () => {
        if (alertState.options.onConfirm) {
            alertState.options.onConfirm();
        }
        hideAlert();
    };

    const handleOk = () => {
        if (alertState.options.onOk) {
            alertState.options.onOk();
        }
        hideAlert();
    };

    return (
        <AlertContext.Provider value={{ showAlert, hideAlert }}>
            {children}
            <Alert
                show={alertState.show}
                message={alertState.message}
                type={alertState.options.type || 'info'}
                title={alertState.options.title}
                onClose={handleClose}
                confirmable={alertState.options.confirmable}
                showOkButton={alertState.options.showOkButton}
                confirmText={alertState.options.confirmText}
                cancelText={alertState.options.cancelText}
                okText={alertState.options.okText}
                onConfirm={handleConfirm}
                onCancel={handleClose} // Default cancel behavior is closing
                onOk={handleOk}
                dismissible={alertState.options.dismissible}
            >
                {null}
            </Alert>
        </AlertContext.Provider>
    );
};
